import React from 'react'
import { HelpDrawerWithAccordion } from '@labrav/common'
import { productDetailsImages } from '../../../common/utils/productCreationUtil'
import {
  FaqQuestion,
  FlyoutProductCreationFaqSection,
  HelpDrawerFaqItems,
} from '../FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'
import { CarouselWithModal } from '../../../common/components/CarouselWithModal/CarouselWithModal'

const faqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.PRODUCT_DETAILS_PRODUCT_DETAILS
        }
        question="Why do I need to fill this page?"
      />
    ),
    answer: (
      <div>
        To list your SaaS product on AWS Marketplace, you must complete the
        Product Details page. The information you provide here is used to create
        and populate your public listing.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.PRODUCT_DETAILS_PRODUCT_DETAILS
        }
        question="Can buyers see the details I provide here?"
      />
    ),
    answer: (
      <div>
        Yes, most of the details you enter will be publicly visible in your AWS
        Marketplace listing. Buyers can view this information when browsing and
        making purchase decisions. However,{' '}
        <strong>SKU and Keywords for search results</strong> are not visible to
        buyers.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.PRODUCT_DETAILS_PRODUCT_DETAILS
        }
        question="What is AWS GovCloud (US) Region?"
      />
    ),
    answer: (
      <div>
        <p>
          AWS GovCloud (US) is a secure cloud region designed for U.S.
          government agencies and customers handling sensitive data. It meets
          strict compliance standards, including:
        </p>
        <ul>
          <li>
            Federal Risk and Authorization Management Program (FedRAMP) High
          </li>
          <li>
            Department of Defense Security Requirements Guide (DoD SRG) Impact
            Levels 4 & 5
          </li>
          <li>Criminal Justice Information Services (CJIS)</li>
        </ul>
        <p>
          To comply with{' '}
          <strong>
            International Traffic in Arms Regulations (ITAR) and Export
            Administration Regulations (EAR)
          </strong>
          , these regions are managed only by U.S. citizens. Learn more.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.PRODUCT_DETAILS_PRODUCT_DETAILS
        }
        question="What should I keep in mind while adding the short description?"
      />
    ),
    answer: (
      <div>
        Your short description should summarize your product in 2-3 sentences.
        If your description exceeds <strong>188 characters</strong>, it will be
        truncated, and buyers will need to click ‘Show more’ to view the full
        text.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.PRODUCT_DETAILS_PRODUCT_DETAILS
        }
        question="What should I add in my long description? Where is it visible?"
      />
    ),
    answer: (
      <div>
        <p>
          Ensure that your long description provides an{' '}
          <strong>in-depth explanation</strong> of your product's features and
          benefits in <strong>1-3 paragraphs</strong>.
        </p>
        <div
          style={{
            backgroundColor: '#E5F6FD',
            padding: '10px',
            borderRadius: '4px',
            marginTop: '10px',
          }}
        >
          <p>
            <strong>Key points:</strong>
          </p>
          <ul>
            <li>
              Do not include references to other cloud platforms, upsell
              services, or free trials unavailable on AWS Marketplace.
            </li>
            <li>
              If your product is an add-on, state that it requires another
              product to function.
            </li>
            <li>
              If listing in AWS GovCloud (US), explain architectural boundaries,
              use cases, and unsupported workloads.
            </li>
            <li>
              Include details about IAM roles, permissions, and AWS services
              used.
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.PRODUCT_DETAILS_PRODUCT_DETAILS
        }
        question="What should I add in the Product Video URL?"
      />
    ),
    answer: (
      <div>
        You can provide a link to an externally hosted, publicly available video
        promoting your product. Example: Any youtube like, open google drive
        link, etc.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.PRODUCT_DETAILS_PRODUCT_DETAILS
        }
        question="What should I keep in mind while adding the Highlights?"
      />
    ),
    answer: (
      <div>
        <p>
          Highlights should be a <strong>bullet-point list</strong> of key
          features and selling points.
        </p>
        <div
          style={{
            backgroundColor: '#E5F6FD',
            padding: '10px',
            borderRadius: '4px',
            marginTop: '10px',
          }}
        >
          <p>
            <strong>Note:</strong> Use 1-2 keywords from your product
            description to improve search rankings.
          </p>
        </div>
      </div>
    ),
  },
]

interface FlyoutProductDetailsFaqsProps {
  indexModal: number
  setIndexModal: (index: number) => void
}

export const FlyoutProductDetailsFaqs: React.FC<
  FlyoutProductDetailsFaqsProps
> = ({ indexModal, setIndexModal }) => (
  <HelpDrawerWithAccordion
    headerTitle="Help & FAQs - Product Details"
    faqItems={faqItems}
    customContent={
      <CarouselWithModal
        carouselData={productDetailsImages}
        setCurrentIndex={setIndexModal}
        currentIndex={indexModal}
      />
    }
  />
)
